import { useState, useEffect } from "react";
import {
  useHMSActions,
  useHMSStore,
  selectIsConnectedToRoom,
  selectPeers,
  useVideo,
} from "@100mslive/react-sdk";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Mic,
  MicOff,
  Video,
  VideoOff,
  Monitor,
  MonitorOff,
  LogOut,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { selectScreenShareByPeerID } from "@100mslive/react-sdk";
import { selectIsLocalScreenShared } from "@100mslive/react-sdk";

// Peer component: render video + name
function Peer({ peer }: { peer: any }) {
  const screenShareTrack = useHMSStore(selectScreenShareByPeerID(peer.id));
  const { videoRef } = useVideo({
    trackId: screenShareTrack?.id || peer.videoTrack,
  });

  // Kiểm tra nếu là local peer và KHÔNG phải chia sẻ màn hình thì mới mirror
  const isLocalCamera = peer.isLocal && !screenShareTrack;

  return (
    <div className="relative flex flex-col items-center w-full">
      <video
        ref={videoRef}
        className={`w-full object-cover rounded-lg shadow-lg mb-2 border-2 border-gray-100 bg-white ${isLocalCamera ? "scale-x-[-1]" : ""}`}
        autoPlay
        muted={peer.isLocal}
        playsInline
        width={160}
        height={160}
      />
      <div
        style={{
          position: "absolute",
          bottom: 10,
          left: 3,
          background: "rgba(0,0,0,0.5)",
          color: "white",
          padding: "4px 12px",
          borderRadius: "6px",
          fontSize: 14,
        }}
      >
        {peer.name} {peer.isLocal ? "(You)" : ""}
      </div>
    </div>
  );
}

// Conference component: render all peers
function Conference() {
  const peers = useHMSStore(selectPeers);
  const { t } = useTranslation();
  return (
    <div className="p-5 mb-28 md:mb-0 md:p-8 max-w-[960px] mx-auto bg-white rounded-lg shadow-md border border-gray-100">
      <h2 className="text-center text-xl pb-2 mb-7 border-b border-gray-100 font-medium text-orange-600">
        {t("title.conference")}
      </h2>
      <div className="mb-4 md:mb-0 grid grid-cols-1 md:grid-cols-2 place-items-center gap-6">
        {peers.map((peer) => (
          <Peer key={peer.id} peer={peer} />
        ))}
      </div>
    </div>
  );
}

function Footer({ onLeave }: { onLeave: () => void }) {
  const { t } = useTranslation();
  const hmsActions = useHMSActions();
  const [isAudioOn, setIsAudioOn] = useState(true);
  const [isVideoOn, setIsVideoOn] = useState(true);
  const [isScreenSharing, setIsScreenSharing] = useState(false);
  const isLocalScreenShared = useHMSStore(selectIsLocalScreenShared);
  const toggleAudio = async () => {
    await hmsActions.setLocalAudioEnabled(!isAudioOn);
    setIsAudioOn(!isAudioOn);
  };

  const toggleVideo = async () => {
    await hmsActions.setLocalVideoEnabled(!isVideoOn);
    setIsVideoOn(!isVideoOn);
  };

  const toggleScreenShare = async () => {
    try {
      await hmsActions.setScreenShareEnabled(!isLocalScreenShared);
    } catch (err: any) {
      if (
        err.name === t("title.cantAccessCaptureDevice") ||
        err.message?.includes(t("title.userDeniedPermission"))
      ) {
        alert(t("title.youNeedToGrantPermission"));
      }
    }
    setIsScreenSharing(!isLocalScreenShared);
  };

  const leaveRoom = async () => {
    await hmsActions.leave();
    onLeave();
  };

  return (
    <div className="flex fixed bottom-0 mb-20 md:mb-0 w-full p-4 justify-center z-10 bg-white border-t border-gray-100 shadow-md">
      <button
        className="w-10 h-10 rounded-full flex items-center justify-center border border-gray-100 bg-white hover:bg-orange-50 text-gray-600 cursor-pointer transition-all duration-150"
        onClick={toggleAudio}
        title={isAudioOn ? t("title.mute") : t("title.unmute")}
      >
        {isAudioOn ? <Mic size={20} /> : <MicOff size={20} />}
      </button>
      <button
        className="w-10 h-10 rounded-full flex items-center justify-center border border-gray-100 bg-white hover:bg-orange-50 text-gray-600 cursor-pointer ml-2 transition-all duration-150"
        onClick={toggleVideo}
        title={isVideoOn ? t("title.hide") : t("title.unhide")}
      >
        {isVideoOn ? <Video size={20} /> : <VideoOff size={20} />}
      </button>
      <button
        className="w-10 h-10 rounded-full flex items-center justify-center border border-gray-100 bg-white hover:bg-orange-50 text-gray-600 cursor-pointer ml-2 transition-all duration-150"
        onClick={toggleScreenShare}
        title={isScreenSharing ? t("title.stopShare") : t("title.shareScreen")}
      >
        {isScreenSharing ? <MonitorOff size={20} /> : <Monitor size={20} />}
      </button>
      <button
        className="rounded px-2 py-1 bg-white text-red-600 cursor-pointer ml-2 border border-red-600 hover:bg-orange-50 transition-all duration-150 flex items-center justify-center"
        onClick={leaveRoom}
        title={t("title.leave")}
      >
        <LogOut size={20} />
      </button>
    </div>
  );
}

// JoinForm: join room form
function JoinForm() {
  const hmsActions = useHMSActions();
  const location = useLocation();
  const { t } = useTranslation();
  const [inputValues, setInputValues] = useState({
    userName: "",
    roomCode: "",
  });
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const roomCode = params.get("roomCode") || "";

    const profileStr = localStorage.getItem("profile");
    let userName = "";
    if (profileStr) {
      try {
        const profile = JSON.parse(profileStr);
        const firstName = profile.firstName || "";
        const lastName = profile.lastName || "";
        userName = `${firstName} ${lastName}`.trim();
      } catch {}
    }

    setInputValues((prev) => ({
      ...prev,
      roomCode,
      userName: userName || prev.userName,
    }));
  }, [location.search]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValues((prevValues) => ({
      ...prevValues,
      [e.target.name]: e.target.value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    const { userName = "", roomCode = "" } = inputValues;
    try {
      const authToken = await hmsActions.getAuthTokenByRoomCode({ roomCode });
      await hmsActions.join({ userName, authToken });
    } catch (e) {
      console.error(e);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const handleBeforeUnload = () => {
      hmsActions.leave();
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [hmsActions]);

  return (
    <form
      onSubmit={handleSubmit}
      className="max-w-[480px] mx-auto my-12 shadow-md rounded-xl p-8 bg-white border border-gray-100"
    >
      <h2 className="mb-6 text-xl font-medium text-orange-500 text-center">
        {t("appointment.joinRoom")}
      </h2>
      <div className="mb-5">
        <label
          htmlFor="userName"
          className="block mb-2 text-sm font-medium text-gray-700"
        >
          {t("input.label.name")}
        </label>
        <input
          required
          value={inputValues.userName}
          onChange={handleInputChange}
          id="userName"
          type="text"
          name="userName"
          placeholder="Your name"
          className="block w-full rounded-lg border border-gray-100 h-10 px-3 bg-white text-gray-700 font-sans placeholder:text-gray-400 focus:outline-none focus:border-orange-200"
        />
      </div>
      {/* <div className="mb-7">
        <input
          required
          value={inputValues.roomCode}
          onChange={handleInputChange}
          id="roomCode"
          type="text"
          name="roomCode"
          placeholder="Room code"
          className="block w-full rounded-lg border border-gray-100 h-10 px-3 bg-white text-gray-700 font-sans placeholder:text-gray-400 focus:outline-none focus:border-orange-200"
        />
      </div> */}
      <button
        className="w-full rounded px-4 py-2 bg-orange-400 text-gray-800 font-medium text-base cursor-pointer hover:bg-orange-100 transition-all duration-150 border border-gray-100 flex items-center justify-center"
        type="submit"
        disabled={isLoading}
      >
        {isLoading ? (
          <>
            <span className="w-5 h-5 mr-2 border-2 border-orange-300 border-t-transparent rounded-full animate-spin"></span>
            {t("button.joining")}
          </>
        ) : (
          t("button.join")
        )}
      </button>
    </form>
  );
}

// Main page logic: show form or conference UI
function JoinFormPage() {
  const isConnected = useHMSStore(selectIsConnectedToRoom);
  const navigate = useNavigate();

  const handleLeave = () => {
    navigate("/");
  };

  return (
    <div className=" bg-white text-gray-600 font-sans">
      {isConnected ? (
        <>
          <Conference />
          <Footer onLeave={handleLeave} />
        </>
      ) : (
        <JoinForm />
      )}
    </div>
  );
}

export default JoinFormPage;
